/*
 * Reusable helper functions
 */

import { KeyboardEvent } from 'react'
import moment from 'moment'
import toast from 'react-hot-toast'
import { ColumnFilters, TableFilters } from './schema/TableColumn'
import { PlatformValue } from './constants'
import { success } from '../api/statusCodes'
import { naiFlowDashboardsTabs } from '../routes/allPaths'

// Local storage stuff start
export const setLocalStorage = (key: string, value: any) => {
  localStorage.setItem(key, value)
}

export const getLocalStorage = (key: string) => {
  if (localStorage.getItem(key)) {
    return localStorage.getItem(key)
  }
  return null
}

// Safely read + parse the stored permissions array. Malformed/absent JSON in
// localStorage must never throw during render — fall back to no permissions.
export const getPermissions = (): any[] => {
  try {
    const parsed = JSON.parse(getLocalStorage('permissions') || '[]')
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export const clearLocalStorage = () => {
  // localStorage.removeItem('refresh_token')
  // localStorage.removeItem('access_token')
  // localStorage.removeItem('user_info')
  // localStorage.removeItem('user_info')
  // localStorage.removeItem('permissions')
  localStorage.clear()
}
// Local storage stuff end

// Authorizations
export const PermittedRoutes = (sidebarLinks: any) => {
  // @TODO: permission part remaining from backend
  const permissions =
    JSON.parse(`${getLocalStorage('permissions')}`) && Array.isArray(JSON.parse(`${getLocalStorage('permissions')}`)) ? JSON.parse(`${getLocalStorage('permissions')}`) : []
  // @TODO: Revisit permission layer for quiz-assessment - currently bypassing permissions for all users
  const byPassRoutes = ['copilots', 'quiz-assessment']

  const filteredLinks = sidebarLinks?.filter((link: any) => byPassRoutes?.includes(link.slug) || permissions?.some((permission: any) => permission.slug === link.slug))

  return filteredLinks
  // return sidebarLinks
}

// Date
export const defaultDateFormat = 'DD/MM/YYYY hh:mm A'
export const formatDate = (date: string | null | Date, format?: string) => {
  return moment(date).format(format || defaultDateFormat)
}

export const fixedMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as any
export const getMonths = (startDate?: any, endDate?: any) => {
  const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']

  const end = endDate ? new Date(endDate) : new Date()
  const start = startDate ? new Date(startDate) : new Date(end.getFullYear(), end.getMonth() - 11, 1)

  let result = []

  for (let d = start; d <= end; d.setMonth(d.getMonth() + 1)) {
    result.push({
      month: months[d.getMonth()],
      year: d.getFullYear(),
    })
  }

  return result
}

export const convertToYYYYMMDD = (dateString: string): string => {
  const [day, month, year] = dateString.split(' ')[0].split('/')
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
}

export const tableFiltersUrl = (tableFilters: TableFilters) => {
  const { pagination, columnFilters, sorting } = tableFilters

  let url = ''
  // Pagination url
  url += `page_number=${pagination?.pageIndex}&page_size=${pagination?.pageSize}`
  // Filter url
  if (columnFilters && columnFilters?.length) {
    columnFilters?.map((column: ColumnFilters) => {
      url += `&${column.id}=${column.value}`
    })
  }
  // Sorting url
  if (sorting && sorting?.length) {
    url += `&sort_by=${sorting[0]?.id}&sort_type=${sorting[0]?.desc ? 'desc' : 'asc'}`
  }
  return url
}

// Order
export const orderByPosition = (data: any) => {
  return refLess(data)?.sort((a: any, b: any) => a?.position - b?.position)
}
export const convertToLabelValue = (data: any[], labelKey?: string, valueLey?: string) => {
  if (!data) return []
  return refLess(data)?.map((item: any) => {
    return {
      label: labelKey ? item[labelKey] : item?.name,
      value: valueLey ? item[valueLey] : item?.id,
      ...item,
    }
  })
}

export const convertToListView = (data: any[], labelKey?: string, valueLey?: string, secondaryValueLey?: string, thirdValueLey?: string) => {
  if (!data) return []
  return refLess(data)?.map((item: any) => {
    return {
      label: labelKey ? item[labelKey] : item?.name,
      value: valueLey ? item[valueLey] : item?.id,
      secondaryValue: secondaryValueLey ? item[secondaryValueLey] : undefined,
      thirdValue: thirdValueLey ? item[thirdValueLey] : undefined,
      ...item,
    }
  })
}

// Reface
export const refLess = (data: any) => {
  if (data) {
    return JSON.parse(JSON.stringify(data))
  }
  return data
}

// String
// Helper function to convert snake_case to readable label
export const convertToLabel = (key: string) => {
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// Alternative camelCase conversion if needed
export const convertToCamelCase = (key: string) => {
  return key.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase())
}

export const trimString = (str: string | number | null | undefined) => {
  return (str ? `${str}` : '')?.trim()
}

export const truncateString = (input: string, len?: number): string => {
  const maxLength = len || 15
  if (input.length > maxLength) {
    return input.substring(0, maxLength) + '...'
  } else {
    return input
  }
}

export const toCamelCase = (str: string) => {
  return str
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (match, chr) => chr.toUpperCase())
    .replace(/^(.)/, (match) => match.toUpperCase())
}

// material-react-table filterFn — case-insensitive substring match that also handles array-valued cells (e.g. hazard_type)
export const columnArrayFilterFn = (row: any, columnId: string, filterValue: any) => {
  const cellValue = row.getValue(columnId) as string[] | string | null
  if (filterValue === '' || filterValue === undefined || filterValue === null) return true

  const filterStr = String(filterValue).toLowerCase()
  if (Array.isArray(cellValue)) {
    return cellValue.some((value) => String(value).toLowerCase().includes(filterStr))
  }

  return String(cellValue ?? '')
    .toLowerCase()
    .includes(filterStr)
}

// Boolean columns render as Yes/No while the cell value stays a boolean, so MRT's default text
// filter compares "Yes" against `true` and never matches — the filter looks dead (NTG-2284).
// Swap in a Yes/No dropdown that matches on the value; true/false is also what the API receives.
export const booleanFilterProps = {
  filterVariant: 'select',
  filterSelectOptions: [
    { label: 'Yes', value: 'true' },
    { label: 'No', value: 'false' },
  ],
  filterFn: (row: any, columnId: string, filterValue: any) =>
    filterValue === '' || filterValue === undefined || filterValue === null ? true : `${row.getValue(columnId)}` === `${filterValue}`,
}

// Applies booleanFilterProps to every column whose data is boolean, leaving explicit filterVariants alone.
export const withBooleanFilters = (columns: any[], data: any[]) =>
  columns?.map((col: any) => {
    const key = col?.accessorKey
    const isBooleanColumn = key && data?.some((row: any) => typeof row?.[key] === 'boolean')
    return isBooleanColumn && !col?.filterVariant ? { ...col, ...booleanFilterProps } : col
  })

// Display text for an auto-generated column's cell. Decides boolean per value rather than from
// data[0]: withBooleanFilters scans every row, so a nullable first row used to pick the
// non-boolean branch and booleans then rendered as 'N/A' under a Yes/No dropdown. Non-boolean
// values keep the original empty -> 'N/A' behaviour.
export const autoCellText = (value: any) => (typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value && value?.length > 0 ? value : 'N/A')

// Http
export const isSuccess = (response: any) => {
  const httpStatus = response?.status
  const httpOk = typeof httpStatus === 'number' && httpStatus >= success && httpStatus < 300
  const metaCode = response?.data?.meta?.code
  return httpOk && Number(metaCode) === success
}
export const showError = (error: any) => {
  if (!error) return
  const raw = error?.data?.meta?.message ?? error?.response?.data?.meta?.message
  const message = typeof raw === 'string' && raw.trim().length > 0 ? raw : 'Something went wrong.'
  toast.error(message)
}

// Input
export const integerOnly = (event: KeyboardEvent<HTMLInputElement>) => {
  // Prevent entering '.' for floating-point numbers
  if (event.key === '.') {
    event.preventDefault()
  }
}

export function debounce(func: Function, wait: number) {
  let timeout: NodeJS.Timeout

  return function (...args: any) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }

    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

// this function helps to find-out that the passed module has the passed permission or not.
// Accepts either:
//   - the raw module array (legacy callers)
//   - the wrapped object { modules: [...], is_admin_user, is_project_admin } (new gateway shape)
//   - an array with is_admin_user / is_project_admin attached as extra properties (intermediate shape)
export const hasPermission = (modules: any, module: any, permission: any) => {
  const arr = Array.isArray(modules) ? modules : modules?.modules
  return arr?.find((m: any) => m?.slug === module)?.permission_ids?.includes(permission)
}

// Case-insensitive match against a `Platform` constant — routes/APIs carry the same platform in different casings (`whatsapp`, `WhatsApp`).
export const isPlatform = (value: unknown, platform: PlatformValue) => String(value ?? '').toLowerCase() === platform.toLowerCase()

export const roundOffNumber = (num: number) => {
  return Number.isInteger(num) ? num : parseFloat(num.toFixed(2))
}

// Colors
export const rgbaToHex = (rgba: string) => {
  const rgbaArray = rgba.match(/\d+/g) as any
  const r = parseInt(rgbaArray[0]).toString(16).padStart(2, '0')
  const g = parseInt(rgbaArray[1]).toString(16).padStart(2, '0')
  const b = parseInt(rgbaArray[2]).toString(16).padStart(2, '0')
  return `#${r}${g}${b}`
}

// Math
export const convertToPercentage = (value: number, min = 0, max = 365) => {
  if (value < min) return 0 // Ensure the value is within range
  if (value > max) return 100 // Cap the percentage at 100%

  const range = max - min
  const percentage = ((value - min) / range) * 100
  return Math.round(percentage) // Round to the nearest integer
}

export const homeScreenRedirection = (defaultPermissions?: any) => {
  const sequentialRoutes = [
    {
      path: '/dashboard/overview',
      slug: 'org-dashboard',
    },
    ...naiFlowDashboardsTabs,
    {
      path: '/user-management/users',
      slug: 'user',
    },
    {
      path: '/user-management/user-role',
      slug: 'user-roles',
    },
    {
      path: '/project-management',
      slug: 'project-management',
    },
    {
      path: '/masters',
      slug: 'master',
    },
  ] as any

  try {
    const permissions = JSON.parse(defaultPermissions || getLocalStorage('permissions') || '[]')

    // Loop through routes sequentially
    for (const route of sequentialRoutes) {
      if (hasPermission(permissions, route.slug, 1)) {
        return route.path
      }
    }

    // If no permissions found, return default
    return '/dashboard'
  } catch (error) {
    console.error('Error in homeScreenRedirection:', error)
    return '/dashboard'
  }
}

/** First nAI Flow dashboard path the user has permission for (Observation, Incident, Inspection, etc.). */
export const getFirstAllowedNaiFlowDashboardPath = (defaultPermissions?: any) => {
  try {
    const permissions = JSON.parse(defaultPermissions || getLocalStorage('permissions') || '[]')
    for (const tab of naiFlowDashboardsTabs) {
      if (hasPermission(permissions, tab.slug, 1)) {
        return tab.path
      }
    }
    return naiFlowDashboardsTabs[0]?.path ?? '/dashboard/nai-flow/observation'
  } catch {
    return '/dashboard/nai-flow/observation'
  }
}

/** First nAI Hub dashboard path the user has permission for. nAI Hub tabs aren't a fixed list —
 *  they're whichever permission modules are flagged `is_nai_hub`, same as the TabsLayout tab strip.
 *  Returns null when the user has none, so callers can fall back instead of routing nowhere. */
export const getFirstAllowedNaiHubDashboardPath = (defaultPermissions?: any) => {
  try {
    // defaultPermissions may arrive as a raw JSON string or an already-parsed array; malformed
    // JSON must not throw here, or the caller can't fall back to its default route.
    const raw = defaultPermissions ?? getPermissions()
    const permissions = typeof raw === 'string' ? JSON.parse(raw) : raw
    const hub = Array.isArray(permissions) ? permissions.find((p: any) => p?.is_nai_hub) : null
    return hub ? `/dashboard/nai-hub/${hub?.module_name}/${hub?.slug}` : null
  } catch {
    return null
  }
}

export const naiFlowAuthTab =
  getLocalStorage('permissions') && JSON.parse(getLocalStorage('permissions') || '') && Array.isArray(JSON.parse(getLocalStorage('permissions') || ''))
    ? JSON.parse(getLocalStorage('permissions') || '')?.find((ls: any) => ls?.slug === 'org-dashboard')
      ? '/dashboard/overview'
      : getFirstAllowedNaiFlowDashboardPath()
    : '/dashboard/nai-flow/observation'
